import { Platform, View, type ViewProps } from "react-native";
import { cardSurfaceStyle, semanticColors } from "../../theme/tokens";

/**
 * The shared card surfaces.
 *
 * - `glass` (default): the page-level card. Radius 16, translucent white, the
 *   glass border and soft shadow.
 * - `panel`: a block inside a card. Use it only where a block needs to read as
 *   separate (a form section, a highlighted summary); rows that simply belong
 *   to the card sit straight in it.
 * - `prompt`: the dashed card that asks for something missing ("Any conditions
 *   or diagnoses to keep with your care?").
 * - `feature`: the shape of a dark textured card, matching the Hero. The caller
 *   draws the texture inside it.
 * - `muted`: a card nested inside a solid surface (a solid dialog), where glass
 *   has no contrast. Faint tint, subtle border, no shadow or blur.
 *
 * `tone="caution"` gives a glass card the warm tint used for warnings
 * ("Avoid or ask first").
 *
 * `tone="onDark"` gives a panel the frosted recipe for a block on a dark card
 * (the Hero's metric pills): white at 10%, a white border at 16%, blur on web.
 * A pressable on-dark panel brightens to `PANEL_ON_DARK_PRESSED_FILL` while
 * pressed.
 *
 * Each tone belongs to one variant (`caution` to glass, `onDark` to panel);
 * any other pairing is a type error.
 *
 * Interactive cards (a Pressable) spread `cardSurface({ variant, tone })`
 * into their own style rather than rebuilding the surface.
 */
export type CardSurfaceVariant =
  "glass" | "panel" | "prompt" | "feature" | "muted";
export type CardSurfaceTone = "default" | "caution" | "onDark";

export type CardSurfaceOptions =
  | {
      variant?: "glass";
      tone?: Extract<CardSurfaceTone, "default" | "caution">;
    }
  | { variant: "panel"; tone?: Extract<CardSurfaceTone, "default" | "onDark"> }
  | { variant: "prompt" | "feature" | "muted"; tone?: "default" };

export const CARD_RADIUS = 16;
export const FEATURE_CARD_RADIUS = 14;
export const PANEL_RADIUS = 12;

export const PANEL_ON_DARK_PRESSED_FILL = semanticColors.onDark.fill.strong;

const CAUTION_TONE = {
  backgroundColor: "rgba(121,70,51,0.07)",
  borderColor: "rgba(121,70,51,0.2)",
};

const PANEL_STYLE = {
  borderRadius: PANEL_RADIUS,
  borderWidth: 1,
  borderColor: "rgba(232,224,216,0.6)",
  backgroundColor: "rgba(255,255,255,0.3)",
};

const PANEL_ON_DARK_STYLE = {
  borderRadius: PANEL_RADIUS,
  borderWidth: 1,
  borderColor: semanticColors.onDark.fill.strong,
  backgroundColor: semanticColors.onDark.fill.subtle,
  ...(Platform.OS === "web" && {
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  }),
};

const PROMPT_STYLE = {
  borderRadius: CARD_RADIUS,
  borderWidth: 1,
  borderStyle: "dashed",
  borderColor: "rgba(92,52,32,0.16)",
  backgroundColor: "rgba(255,255,255,0.32)",
};

const MUTED_STYLE = {
  ...cardSurfaceStyle,
  backgroundColor: semanticColors.surface.muted,
  borderColor: semanticColors.border.subtle,
  shadowOpacity: 0,
  ...(Platform.OS === "web" && {
    backdropFilter: "none",
    WebkitBackdropFilter: "none",
  }),
};

const FEATURE_STYLE = {
  borderRadius: FEATURE_CARD_RADIUS,
  overflow: "hidden",
};

function resolveCardSurface(
  variant: CardSurfaceVariant = "glass",
  tone: CardSurfaceTone = "default",
): Record<string, unknown> {
  switch (variant) {
    case "panel":
      return tone === "onDark" ? PANEL_ON_DARK_STYLE : PANEL_STYLE;
    case "prompt":
      return PROMPT_STYLE;
    case "feature":
      return FEATURE_STYLE;
    case "muted":
      return MUTED_STYLE;
    default:
      return tone === "caution"
        ? { ...cardSurfaceStyle, ...CAUTION_TONE }
        : cardSurfaceStyle;
  }
}

export function cardSurface(
  options: CardSurfaceOptions = {},
): Record<string, unknown> {
  return resolveCardSurface(options.variant, options.tone);
}

export function CardSurface({
  variant,
  tone,
  style,
  ...props
}: ViewProps & CardSurfaceOptions) {
  return <View {...props} style={[resolveCardSurface(variant, tone), style]} />;
}
